import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Grid, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SectionCard from '../inventory/components/SectionCard';
import InvButton from '../inventory/components/InvButton';
import { InvSelect, InvTextField } from '../inventory/components/InvField';
import { fetchMedicalDepartments, fetchDoctorsByDepartment } from '../ipd/pages/patient-admission-ipd/api/registrationService';
import { IPD_COLORS } from '../inventory/theme';
import { OT_SECTION_SX } from './otTheme';
import { IPD_FIELD_HEIGHT } from '../ipd/utils/ipdFieldStyles';

/** Mirrors IpdFormField label + helper spacing so action icons line up with inputs. */
function FieldActionSlot({ children }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: IPD_FIELD_HEIGHT, flexShrink: 0 }}>
      <Box
        aria-hidden
        sx={{
          mb: 0.75,
          fontSize: '0.8125rem',
          fontWeight: 600,
          lineHeight: 1.4,
          visibility: 'hidden',
          userSelect: 'none',
        }}
      >
        &nbsp;
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: IPD_FIELD_HEIGHT }}>
        {children}
      </Box>
      <Box aria-hidden sx={{ minHeight: 20, mt: 0.25 }} />
    </Box>
  );
}

export function newDoctorRow() {
  return {
    key: `doc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    department: '',
    doctorId: '',
    doctorName: '',
    amount: '',
  };
}

export default function DepartmentDoctorSection({ doctors, onDoctorsChange }) {
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [doctorsByDept, setDoctorsByDept] = useState({});
  const [loadingDept, setLoadingDept] = useState('');
  const fetchedDeptRef = useRef({});

  useEffect(() => {
    fetchMedicalDepartments()
      .then(({ options }) => setDepartmentOptions(options.map(d => ({ value: d.departmentName, label: d.departmentName }))))
      .catch(() => setDepartmentOptions([]));
  }, []);

  const loadDoctors = useCallback((department) => {
    if (!department || fetchedDeptRef.current[department]) return;
    fetchedDeptRef.current[department] = true;
    setLoadingDept(department);
    fetchDoctorsByDepartment(department)
      .then((list) => {
        setDoctorsByDept(prev => ({
          ...prev,
          [department]: list.map(d => ({ value: d.doctor_id, label: d.name })),
        }));
      })
      .catch(() => {
        fetchedDeptRef.current[department] = false;
        setDoctorsByDept(prev => ({ ...prev, [department]: [] }));
      })
      .finally(() => setLoadingDept(prev => (prev === department ? '' : prev)));
  }, []);

  const updateRow = (key, patch) => {
    onDoctorsChange(doctors.map(row => (row.key === key ? { ...row, ...patch } : row)));
  };

  const selectedDoctorIds = new Set(doctors.map(row => row.doctorId).filter(Boolean));

  return (
    <SectionCard title="Department & Doctor Selection" sx={OT_SECTION_SX}>
      {doctors.map((row) => {
        const options = (doctorsByDept[row.department] || []).filter(
          opt => opt.value === row.doctorId || !selectedDoctorIds.has(opt.value),
        );
        return (
          <Grid container spacing={2} key={row.key} sx={{ mb: 1 }} alignItems="flex-start">
            <Grid item xs={12} sm={4}>
              <InvSelect
                label="Department"
                value={row.department}
                onChange={e => {
                  const department = e.target.value;
                  updateRow(row.key, { department, doctorId: '', doctorName: '' });
                  loadDoctors(department);
                }}
                options={departmentOptions}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <InvSelect
                label="Doctor"
                value={row.doctorId}
                onChange={e => {
                  const doctorId = e.target.value;
                  const match = options.find(o => o.value === doctorId);
                  updateRow(row.key, { doctorId, doctorName: match?.label || '' });
                }}
                options={options}
                disabled={!row.department || loadingDept === row.department}
              />
            </Grid>
            <Grid item xs={12} sm={doctors.length > 1 ? 3 : 4}>
              <InvTextField
                label="Billing amount"
                type="number"
                value={row.amount}
                onChange={e => updateRow(row.key, { amount: e.target.value })}
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>
            {doctors.length > 1 && (
              <Grid item xs={12} sm="auto">
                <FieldActionSlot>
                  <IconButton
                    size="small"
                    aria-label="Remove doctor"
                    onClick={() => onDoctorsChange(doctors.filter(r => r.key !== row.key))}
                    sx={{
                      color: IPD_COLORS.error.text,
                      width: IPD_FIELD_HEIGHT,
                      height: IPD_FIELD_HEIGHT,
                      border: `1px solid ${IPD_COLORS.border}`,
                      borderRadius: '10px',
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </FieldActionSlot>
              </Grid>
            )}
          </Grid>
        );
      })}

      <Box sx={{ mt: 0.5 }}>
        <InvButton
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => onDoctorsChange([...doctors, newDoctorRow()])}
        >
          Add doctor
        </InvButton>
      </Box>
    </SectionCard>
  );
}
